import Button from 'components/ui/Button/Button';
import { showError, showSuccess } from 'utils/utils';
import { faWhatsapp, faTelegram } from '@fortawesome/free-brands-svg-icons';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { FC, RefObject, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getProposalPdfByUrl } from 'services/proposal';
import { getTripBuilderPdfBody } from 'utils/shared/tripBuilderClientSummary';
import { sendEmailProposalPublic } from 'services/cms';
import useRecaptcha from 'hooks/useRecaptcha';
import LoaderMain from '../../LoaderMain';
import { i18nTB } from 'utils/shared/utils';
import { MainContext } from 'contexts/MainContext';
import { cdnUrl } from 'constants/generic';

interface TripBuilderShareActionsProps {
  proposalId: string;
  pdfContainerRef: RefObject<HTMLElement | null>;
  hotelNames?: string[];
  disabled?: boolean;
  plain?: boolean;
  embedded?: boolean;
}

const TripBuilderShareActions: FC<TripBuilderShareActionsProps> = ({
  proposalId,
  pdfContainerRef,
  hotelNames = [],
  disabled,
  plain,
  embedded,
}) => {
  const { t } = useTranslation(undefined, { i18n: i18nTB });
  const { user } = useContext(MainContext);
  const { handleRecapcha } = useRecaptcha();
  const [loading, setLoading] = useState(false);
  const [loadingPrint, setLoadingPrint] = useState(false);
  const [loadingShare, setLoadingShare] = useState<'whatsapp' | 'telegram' | null>(null);

  const getPdfUrl = async (): Promise<string | null> => {
    if (!proposalId) {
      return null;
    }

    const recaptchaToken = await handleRecapcha();
    if (!recaptchaToken) {
      return null;
    }

    const res = await getProposalPdfByUrl({
      previewKeyPublic: proposalId,
      recaptchaToken,
      ...getTripBuilderPdfBody(pdfContainerRef.current),
    });

    if (!res?.pdfUrl) {
      return null;
    }

    return res.pdfUrl.replace(
      's3.eu-west-1.amazonaws.com/prod.beprotravel',
      window.location.host + '/backend/data/download',
    );
  };

  const onClickPrint = async () => {
    if (!proposalId) {
      return;
    }

    setLoadingPrint(true);

    const recaptchaToken = await handleRecapcha();
    if (!recaptchaToken) {
      setLoadingPrint(false);
      return;
    }

    const res = await getProposalPdfByUrl({
      previewKeyPublic: proposalId,
      recaptchaToken,
      ...getTripBuilderPdfBody(pdfContainerRef.current),
    });

    if (res?.pdfUrl) {
      window.open(res.pdfUrl, '_blank');
    } else {
      showError(t('something_went_wrong'));
    }

    setLoadingPrint(false);
  };

  const onClickSendEmail = async () => {
    if (!proposalId) {
      return;
    }

    setLoading(true);
    const urlClear = await getPdfUrl();

    const email = user?.email || '';

    if (!urlClear) {
      setLoading(false);
      return;
    }

    const proposalUrl = `${cdnUrl}${window.location.pathname}${window.location.search}`;
    const response = await sendEmailProposalPublic(email, urlClear, proposalId, hotelNames, proposalUrl);

    if (response && !response?.hasErrors) {
      showSuccess(t('mo.email_success'));
    } else {
      showError(t('something_went_wrong'));
    }

    setLoading(false);
  };

  const onClickShareWhatsApp = async () => {
    setLoadingShare('whatsapp');
    const urlClear = await getPdfUrl();

    if (!urlClear) {
      setLoadingShare(null);
      return;
    }

    const proposalUrl = `${cdnUrl}${window.location.pathname}${window.location.search}`;
    const message = t('whatsapp_message', { url: urlClear, link: proposalUrl });
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    setLoadingShare(null);
  };

  const onClickShareTelegram = async () => {
    setLoadingShare('telegram');
    const urlClear = await getPdfUrl();

    if (!urlClear) {
      setLoadingShare(null);
      return;
    }

    const message = t('telegram_message', { url: urlClear, siteUrl: window.location.href });
    const url = `https://t.me/share/url?url=${encodeURIComponent(urlClear)}&text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    setLoadingShare(null);
  };

  const barContent = (
    <>
      <div className="pr-actions-left">
        <Button
          className="btn btn-primary pr-actions-btn"
          title={t('send_to_agent')}
          onClick={onClickSendEmail}
          disabled={disabled || loading}
        />
      </div>

      <div className="pr-actions-right">
        <Button
          title=""
          icon={faDownload}
          onClick={onClickPrint}
          className="btn btn-primary pr-actions-btn"
          disabled={disabled || loadingPrint}
        />
        <Button
          className="btn btn-whatsapp pr-actions-btn"
          title=""
          icon={faWhatsapp}
          onClick={onClickShareWhatsApp}
          disabled={disabled || loadingShare === 'whatsapp'}
        />
        <Button
          className="btn btn-telegram pr-actions-btn"
          title=""
          icon={faTelegram}
          onClick={onClickShareTelegram}
          disabled={disabled || loadingShare === 'telegram'}
        />
      </div>
    </>
  );

  const loader = (loadingShare || loadingPrint || loading) && (
    <div className="pr-actions-loader">
      <LoaderMain systemProducts="Hotels" />
    </div>
  );

  if (embedded) {
    return (
      <>
        <div className="display-flex w100p flex-between">{barContent}</div>
        {loader}
      </>
    );
  }

  return (
    <div className={'pr-actions' + (plain ? ' pr-actions-plain' : '')}>
      <div className={'pr-actions-bar' + (plain ? ' pr-actions-bar-plain' : '')}>
        {barContent}
      </div>
      {loader}
    </div>
  );
};

export default TripBuilderShareActions;
